import { useNavigate } from "react-router-dom";

import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CheckCircle2, Loader2, Lightbulb, Landmark } from "lucide-react";
import { toast } from "sonner";

import {
    endPointSubscriptionCreate,
    endPointCheckBenefit,
} from "modules/main/services/endpoints";
import { useAuth } from "modules/authentication/hooks/use-auth";
import { MainLogo } from "modules/main/assets/main_logo";
import { formatRut, validateRut } from "utils/rut";
import {
    fetchSubscriptionPreview,
    REGULAR_SUBSCRIPTION_PREVIEW,
    SubscriptionPreview,
} from "../services/subscription-preview";

/**
 * Props for {@link AcquirePlusDialog}.
 */
export interface AcquirePlusDialogProps {
    /** Controls dialog visibility. */
    open: boolean;
    /** Called when the dialog requests an open-state change (e.g. X button or overlay click). */
    onOpenChange: (open: boolean) => void;
    /** Cut 3: which free-plan quota was exceeded (quota-enforcement contract). */
    reason?: "weekly_list_quote_limit" | "monthly_optimization_limit";
    /** Cut 3: usage metadata for quota-specific copy. */
    quota?: { used: number; limit: number; reset_at?: string };
}

/**
 * Internal type for subscription creation API response.
 *
 * Backend returns two mutually exclusive shapes:
 * - Normal flow: { url, token } — redirect popup to Flow payment form
 * - Already active: { already_active: true, redirect } — navigate to already-active page
 */
type SubscriptionCreateResponse =
    | { url: string; token: string; already_active?: never; redirect?: never }
    | { already_active: true; redirect: string; url?: never; token?: never };

interface BenefitValidationResponse {
    has_benefit: boolean;
    message: string | null;
    trial_days: number | null;
    amount: number | null;
    price_label: string | null;
    confirmation_title: string | null;
    confirmation_description: string | null;
    activated: boolean;
    plan_type: string | null;
    partner_name: string | null;
    partner_logo_url: string | null;
    benefit_expires_at: string | null;
}

const BENEFITS = [
    "Optimiza compras de cualquier monto",
    "Crea carritos ilimitados",
    "Organiza tus compras en listas ilimitadas",
    "Prueba las novedades antes que nadie",
    "Accede a beneficios exclusivos de Carriapp Plus",
];

const formatBenefitDate = (date: string | null): string | null => {
    if (!date) {
        return null;
    }

    const [year, month, day] = date.split("-").map(Number);
    if (!year || !month || !day) {
        return null;
    }

    return new Intl.DateTimeFormat("es-CL", {
        day: "numeric",
        month: "long",
        year: "numeric",
    }).format(new Date(year, month - 1, day));
};

const formatTrialDuration = (days: number | null): string | null => {
    if (!days) {
        return null;
    }

    if (days % 30 === 0) {
        const months = days / 30;
        return `${months} ${months === 1 ? "mes" : "meses"}`;
    }

    return `${days} ${days === 1 ? "día" : "días"}`;
};

/**
 * Returns the free trial copy string when trial_days > 0, otherwise null.
 * Used to conditionally render "X días gratis" in the upsell dialog.
 */
const getFreeTrialCopy = (trialDays: number | null | undefined): string | null => {
    if (!trialDays || trialDays <= 0) {
        return null;
    }
    return `${trialDays} días gratis`;
};

const formatMonthlyPrice = (priceLabel: string | null): string | null => {
    if (!priceLabel) {
        return null;
    }

    return `${priceLabel}/mes`;
};

/**
 * Builds the quota-specific upsell banner copy (Cut 3).
 * @param reason Which quota was exceeded.
 * @param quota Usage metadata (limit may be absent on legacy bodies).
 * @returns Spanish banner text, or null when no quota reason applies.
 */
const quotaBlockCopy = (
    reason?: "weekly_list_quote_limit" | "monthly_optimization_limit",
    quota?: { used: number; limit: number; reset_at?: string },
): string | null => {
    if (reason === "weekly_list_quote_limit") {
        return `Alcanzaste tu límite de ${quota?.limit ?? 4} cotizaciones semanales del plan gratuito.`;
    }
    if (reason === "monthly_optimization_limit") {
        return `Ya usaste las optimizaciones gratis de este mes.`;
    }
    return null;
};

const appendBenefitRetryMessage = (message: string): string => {
    const separator = /[.!?]$/.test(message.trim()) ? " " : ". ";

    return `${message}${separator}Intenta de nuevo en unos minutos`;
};

/**
 * Controlled upsell dialog displayed to non-Plus users when their cart total
import { Badge } from "@/components/ui/badge";
 * exceeds the $300.000 CLP optimization threshold.
 *
 * Executes the full subscription creation flow internally when the CTA is clicked:
 * - Opens a blank popup immediately (preserves user gesture for payment redirect)
 * - Calls /api/subscription/create backend endpoint
 * - Shows loading state while awaiting response
 * - Redirects popup to payment_url on success
 * - Shows error toast and resets on failure
 *
 * @param open - Whether the dialog is currently visible.
 * @param onOpenChange - Callback to update the open state.
 */
export const AcquirePlusDialog = ({
    open,
    onOpenChange,
    reason,
    quota,
}: AcquirePlusDialogProps): JSX.Element => {
    const [isConnecting, setIsConnecting] = useState(false);
    const [isWaitingConfirmation, setIsWaitingConfirmation] = useState(false);
    const [rut, setRut] = useState("");
    const [preview, setPreview] = useState<SubscriptionPreview>(REGULAR_SUBSCRIPTION_PREVIEW);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [isValidatingRut, setIsValidatingRut] = useState(false);
    const { refreshSubscriptionStatus } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        let isMounted = true;

        const loadPreview = async () => {
            if (!open) {
                return;
            }

            setIsPreviewLoading(true);
            try {
                const nextPreview = await fetchSubscriptionPreview();
                if (isMounted) {
                    setPreview(nextPreview);
                }
            } catch {
                if (isMounted) {
                    setPreview(REGULAR_SUBSCRIPTION_PREVIEW);
                }
            } finally {
                if (isMounted) {
                    setIsPreviewLoading(false);
                }
            }
        };

        void loadPreview();

        return () => {
            isMounted = false;
        };
    }, [open]);

    // Listen for SUBSCRIPTION_OK message from popup windows
    useEffect(() => {
        const handleMessage = async (event: MessageEvent) => {
            if (event.data?.type === "SUBSCRIPTION_OK") {
                await refreshSubscriptionStatus();
                setIsWaitingConfirmation(false);
                onOpenChange(false);
            }
        };

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [refreshSubscriptionStatus, onOpenChange]);

    const [rutValidation, setRutValidation] = useState<"idle" | "valid" | "invalid">("idle");
    const [benefitMessage, setBenefitMessage] = useState<string | null>(null);
    const [benefitConfirmation, setBenefitConfirmation] = useState<BenefitValidationResponse | null>(null);
    const [isClosingBenefitConfirmation, setIsClosingBenefitConfirmation] = useState(false);
    const trialDuration = formatTrialDuration(benefitConfirmation?.trial_days ?? null);
    const formattedBenefitEndDate = formatBenefitDate(benefitConfirmation?.benefit_expires_at ?? null);
    const monthlyPrice = formatMonthlyPrice(benefitConfirmation?.price_label ?? null);
    const partnerName = benefitConfirmation?.partner_name || "tu caja de compensación";
    const isCorporateDiscountedPreview = preview.eligible && preview.offer_type === "corporate_discounted";

    const handleValidateRut = async () => {
        const cleanRut = rut.replace(/[^0-9kK]/g, "");
        const isValid = validateRut(rut);
        if (!isValid) {
            setRutValidation("invalid");
            return;
        }
        setIsValidatingRut(true);
        setRutValidation("idle");
        setBenefitMessage(null);

        try {
            const response = await fetch(endPointCheckBenefit, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ rut: cleanRut }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const errorMessage =
                    errorData.detail || "Error al verificar el RUT.";
                throw new Error(errorMessage);
            }

            const data: BenefitValidationResponse = await response.json();
            if (data.has_benefit === false) {
                setBenefitMessage(data.message || "El usuario no tiene un beneficio asociado a este RUT.");
                return;
            }

            setRutValidation("valid");
            setBenefitConfirmation(data);
        } catch (error) {
            const message =
                error instanceof Error ? error.message : "Error de conexión";
            toast.error(appendBenefitRetryMessage(message));
        } finally {
            setIsValidatingRut(false);
        }
    };

    const handleBenefitConfirmationClose = async () => {
        if (isClosingBenefitConfirmation) {
            return;
        }

        setIsClosingBenefitConfirmation(true);

        try {
            await refreshSubscriptionStatus();
        } finally {
            setIsClosingBenefitConfirmation(false);
            setBenefitConfirmation(null);
            onOpenChange(false);
        }
    };

    const handleConnect = async () => {
        // Prevent double-click
        if (isConnecting) {
            return;
        }

        // Open popup immediately to preserve user gesture
        const popup = window.open("", "_blank");

        if (!popup) {
            toast.error("Por favor permite ventanas emergentes para continuar.");
            return;
        }

        setIsConnecting(true);

        try {
            const response = await fetch(endPointSubscriptionCreate, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const errorMessage =
                    errorData.detail || "Error al conectar con el canal de pago.";
                throw new Error(errorMessage);
            }

            const data: SubscriptionCreateResponse = await response.json();

            // Check if user already has active subscription
            if ("redirect" in data ) {
                // Close popup and redirect
                popup.close();
                navigate(data.redirect??"/store");
                onOpenChange(false);
                return;
            }

            // Normal flow: redirect popup to Flow payment URL with token
            popup.location.href = `${data.url}?token=${data.token}`;

            // Keep dialog open waiting for confirmation from popup
            setIsConnecting(false);
            setIsWaitingConfirmation(true);
        } catch (error) {
            // Close popup on failure
            popup.close();

            // Show error toast
            const message =
                error instanceof Error ? error.message : "Error de conexión";
            toast.error(message);
        } finally {
            setIsConnecting(false);
        }
    };

    return (
        <>
        <Dialog
            open={open && !benefitConfirmation}
            onOpenChange={(value) => {
                // Prevent closing dialog while waiting for payment confirmation
                if (isWaitingConfirmation && !value) return;
                onOpenChange(value);
            }}
        >
            <DialogContent className="sm:max-w-md pt-8">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-extrabold leading-tight text-left">
                        <span
                            className={`font-bold russo-one-regular logo-text text-2xl text-foreground`}
                        >
                            Carriapp
                        </span>
                        <span className="inline-flex h-4 ml-2 items-center rounded-full border border-border/50 bg-background px-1.5 text-[20px] font-semibold leading-none">
                            <span className="font-semibold bg-[linear-gradient(90deg,#F9802D_0%,#F9802D_42%,#5CA273_100%)] bg-[length:220%_220%] bg-clip-text text-transparent animate-[ai-border-flow_8s_cubic-bezier(0.4,0,0.2,1)_infinite]">
                            +Plus
                            </span>
                        </span> <span>{preview.headline.replace(/^Carriapp,?\s*/i, "")}</span>
                    </DialogTitle>
                    <DialogDescription className="text-base text-muted-foreground mt-1">
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 my-4">
                    {quotaBlockCopy(reason, quota) && (
                        <div className="rounded-lg border border-primary/20 bg-primary/10 px-3 py-2 text-sm font-medium text-primary">
                            <Lightbulb size={16} className="inline mr-2" />
                            {quotaBlockCopy(reason, quota)}
                        </div>
                    )}
                    {isPreviewLoading && (
                        <div className="rounded-lg border border-border/50 bg-muted px-3 py-2 text-sm font-medium text-muted-foreground">
                            <Loader2 size={16} className="inline mr-2 animate-spin" />
                            Cargando beneficio disponible...
                        </div>
                    )}
                    {preview.eligible && (
                        <div className="rounded-lg border border-primary/20 bg-primary/10 px-3 py-2 text-sm font-medium text-primary">
                            {isCorporateDiscountedPreview && (preview.partner_logo_url || preview.partner_name) ? (
                                <div className="flex items-center gap-3">
                                    {preview.partner_logo_url && (
                                        <img
                                            alt={preview.partner_name || "Corporate partner"}
                                            className="h-9 max-w-[120px] rounded-md border bg-background object-contain px-2 py-1"
                                            src={preview.partner_logo_url}
                                        />
                                    )}
                                    <div className="flex flex-col">
                                        {preview.badge_label && <span>{preview.badge_label}</span>}
                                        {preview.partner_name && <span className="text-xs text-primary/80">{preview.partner_name}</span>}
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <CheckCircle2 size={16} className="inline mr-2" />
                                    {preview.badge_label || "Convenio Plus"}
                                </>
                            )}
                        </div>
                    )}
                    {BENEFITS.map((benefit) => (
                        <div key={benefit} className="flex items-start gap-4">
                            <CheckCircle2
                                size={20}
                                data-testid="plus-benefit-icon"
                                className="text-primary mt-0.5 shrink-0"
                            />
                            <span className="text-sm font-semibold
                            ">{benefit}</span>
                        </div>
                    ))}
                </div>

                <div className="bg-primary/10 rounded-lg p-3 text-sm font-medium text-center text-primary">
                    <Lightbulb size={16} className="inline mr-2" />
                    {preview.roi_hook}
                </div>
                <div>
                    <span className="text-3xl font-bold text-foreground">{preview.display_price.split(" /")[0]}</span>
                    <span className="text-sm font-bold text-muted-foreground">{preview.display_price.includes(" /") ? ` /${preview.display_price.split(" /")[1]}` : ""}</span>
                </div>
                {getFreeTrialCopy(preview.trial_days) && (
                    <div className="text-center">
                        <span className="text-xl font-bold text-foreground">
                            Pruébalo gratis por {preview.trial_days} días
                        </span>
                    </div>
                )}
                <DialogFooter className="flex-col gap-2 mt-0 sm:flex-col">
                    {isWaitingConfirmation ? (
                        <div className="bg-primary/10 rounded-lg p-4 text-sm font-medium text-center text-primary">
                            <Loader2 size={16} className="inline mr-2 animate-spin" />
                            Esperando confirmación de registro...
                        </div>
                    ) : (
                        <>
                            <Button
                                variant="default"
                                size="lg"
                                className="text-base px-4"
                                onClick={handleConnect}
                                disabled={isConnecting}
                            >
                                {isConnecting ? (
                                    <>
                                        <Loader2 size={16} className="mr-2 animate-spin" />
                                        Conectando con canal de pago...
                                    </>
                                ) : (
                                    <>
                                        {getFreeTrialCopy(preview.trial_days)
                                            ? `Probar ${preview.trial_days} días gratis`
                                            : "Suscribirse"}
                                    </>
                                )}
                            </Button>
                            <DialogClose asChild>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="w-full text-muted-foreground"
                                    disabled={isConnecting}
                                >
                                    Continuar sin Carriapp Plus
                                </Button>
                            </DialogClose>
                        </>
                    )}
                   {!isCorporateDiscountedPreview && <Accordion type="single" collapsible className="w-full">
                      <AccordionItem value="company-benefits" className="border-border/50">
                          <AccordionTrigger className="text-sm font-medium text-muted-foreground hover:text-foreground py-3">
                            <span className="flex items-center gap-2"><Landmark /> ¿Eres afiliado a la Caja la Araucana?</span>
                          </AccordionTrigger>
                          <AccordionContent className="space-y-3 pb-4">
                              <p className="text-xs text-muted-foreground">
                                  Ingresa tu RUT para activar el convenio disponible.
                              </p>
                               {benefitMessage && (
                                    <p className="text-xs text-destructive font-medium flex items-center gap-1">
                                        <Landmark size={12} /> {benefitMessage}
                                    </p>
                                )}
                                {rutValidation === "invalid" && (
                                    <p className="text-xs text-destructive font-medium">
                                        RUT inválido. Verifica el formato y dígito verificador.
                                    </p>
                                )}
                                {rutValidation === "valid" && (
                                    <p className="text-xs text-green-600 font-medium flex items-center gap-1">
                                        <CheckCircle2 size={12} /> RUT válido
                                    </p>
                                )}
                               <div className="flex gap-2">
                                   <Input
                                       placeholder="Ej: 12.345.678-9"
                                       value={rut}
                                        onChange={(e) => {
                                            setRut(formatRut(e.target.value));
                                            setRutValidation("idle");
                                            setBenefitMessage(null);
                                        }}
                                       className={`flex-1 ${rutValidation === "invalid" ? "border-destructive ring-destructive/20" : ""} ${rutValidation === "valid" ? "border-green-500 ring-green-500/20" : ""}`}
                                   />
                                  <Button
                                      variant="outline"
                                      size="sm"
                                      disabled={isValidatingRut || !rut.trim()}
                                      onClick={handleValidateRut}
                                  >
                                      {isValidatingRut ? (
                                          <Loader2 size={14} className="animate-spin" />
                                      ) : (
                                          "Validar"
                                      )}
                                  </Button>
                              </div>
                          </AccordionContent>
                      </AccordionItem>
                   </Accordion>}                   
                </DialogFooter>
            </DialogContent>
        </Dialog>
        <Dialog
            open={Boolean(benefitConfirmation)}
            onOpenChange={(value) => {
                if (!value) {
                    void handleBenefitConfirmationClose();
                }
            }}
        >
            <DialogContent className="sm:max-w-md pt-8">
                <DialogHeader>
                    <div className="mb-3 flex items-center justify-center gap-4">
                        <div className="flex items-center gap-2 rounded-lg border bg-background px-3 py-2 shadow-sm" aria-label="Carriapp">
                            <MainLogo classname="h-7 w-7" />
                            <span className="font-bold russo-one-regular logo-text text-sm text-foreground">Carriapp</span>
                        </div>
                        {benefitConfirmation?.partner_logo_url && (
                            <img
                                alt={benefitConfirmation.partner_name || "Logo del convenio"}
                                className="h-11 max-w-[150px] rounded-lg border bg-background object-contain px-3 py-2 shadow-sm"
                                src={benefitConfirmation.partner_logo_url}
                            />
                        )}
                    </div>
                    <DialogTitle className="text-center text-2xl font-extrabold leading-tight">
                        ¡Bienvenido a Carriapp Plus!
                    </DialogTitle>
                    <DialogDescription className="text-center text-base text-muted-foreground mt-2">
                        {trialDuration && monthlyPrice
                            ? `Como afiliado de ${partnerName} tienes acceso gratuito por ${trialDuration}, luego ${monthlyPrice}.`
                            : benefitConfirmation?.confirmation_description || "Tu beneficio Carriapp Plus fue activado correctamente."}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 rounded-xl border border-primary/20 bg-primary/10 p-4 text-sm">
                    <div className="flex items-center gap-2 font-semibold text-primary">
                        <CheckCircle2 size={18} />
                        {benefitConfirmation?.confirmation_title || "Beneficio activo"}
                    </div>
                    <div className="space-y-2 text-muted-foreground">
                        {formattedBenefitEndDate && (
                            <p>
                                Gratis hasta: <span className="font-bold text-foreground">{formattedBenefitEndDate}</span>
                            </p>
                        )}
                        {monthlyPrice && (
                            <p>
                                Después: <span className="font-bold text-foreground">{monthlyPrice}</span>
                            </p>
                        )}
                    </div>
                    <p className="pt-2 text-muted-foreground">
                        Puedes cancelar cuando quieras desde tu perfil, sin costo.
                    </p>
                </div>

                <DialogFooter>
                    <Button
                        className="w-full"
                        disabled={isClosingBenefitConfirmation}
                        onClick={() => void handleBenefitConfirmationClose()}
                    >
                        {isClosingBenefitConfirmation ? "Actualizando..." : "Entendido"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
        </>
    );
};
